// @ts-strict-ignore
import Drawer from 'antd/lib/drawer';
import React from 'react';
import { useIsMobile } from '../../../mediaQueriesHelpers';
import { antGrey } from '../../styles/globalStyles';
import { useWindowDimensions } from './useWindowDimensions';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import Collapse from 'antd/lib/collapse';
import List from 'antd/lib/list';
import Typography from 'antd/lib/typography';
import { compact } from 'lodash';

export interface ResponsiveDrawerProps {
    open: boolean;
    header?: React.ReactNode;
    content?: React.ReactNode;
    onClose?: () => void;
    afterOpenChange?: (boolean) => void;
    heightRatio?: number;
    canCollapseOnMobile?: boolean;
}

export const ResponsiveDrawer: React.FC<ResponsiveDrawerProps> = ({
    header,
    content,
    open,
    onClose,
    afterOpenChange,
    heightRatio,
    canCollapseOnMobile,
}) => {
    const isMobile = useIsMobile();

    const { height: windowHeight } = useWindowDimensions();

    const height = windowHeight * (heightRatio ?? 0.9);
    const padContentOnMobile = isMobile && !canCollapseOnMobile;

    return (
        <Drawer
            afterOpenChange={afterOpenChange}
            closable={false}
            headerStyle={{
                padding: 0,
            }}
            destroyOnClose
            bodyStyle={{
                padding: padContentOnMobile ? 20 : 0,
            }}
            onClose={onClose}
            title={
                header && (
                    <div
                        style={{
                            backgroundColor: antGrey.grey2,
                            padding: 20,
                        }}
                    >
                        {header}
                    </div>
                )
            }
            placement={isMobile ? 'bottom' : 'right'}
            open={open}
            height={height}
            width={600}
        >
            {content}
        </Drawer>
    );
};

export interface DrawerSectionProps {
    title: string;
    startsCollapsed?: boolean;
    extra?: React.ReactNode;
    children: React.ReactNode;
    headerExtra?: React.ReactNode;
    canCollapseOnMobile?: boolean;
}

export const DrawerSection: React.FC<DrawerSectionProps> = ({
    title,
    startsCollapsed,
    children,
    extra,
    headerExtra,
    canCollapseOnMobile,
}) => {
    const isMobile = useIsMobile();
    const [isCollapsed, setIsCollapsed] = React.useState(startsCollapsed ?? false);

    const titleComponent = (
        <Space>
            <Text
                style={{
                    fontSize: 12,
                    color: antGrey.grey7,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                }}
            >
                {title}
            </Text>
        </Space>
    );

    const collapseWrapper = (children: React.ReactNode) => (
        <Collapse
            defaultActiveKey={startsCollapsed ? ['0'] : ['1']}
            onChange={(key) => {
                const isOpen = key === '1' || (Array.isArray(key) && key.includes('1'));
                setIsCollapsed(!isOpen);
            }}
            className="drawer-section-collapse"
            style={{
                paddingTop: isMobile ? 0 : 12,
                paddingBottom: isMobile ? 0 : 12,
            }}
            ghost
        >
            <Collapse.Panel key="1" header={titleComponent} extra={!isCollapsed && headerExtra}>
                {children}
            </Collapse.Panel>
        </Collapse>
    );

    const content = (
        <div
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
            }}
        >
            {isMobile && !canCollapseOnMobile ?
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Space>
                        {titleComponent}
                        {headerExtra}
                    </Space>
                    {extra}
                </div>
            :   extra}
            {children}
        </div>
    );

    return isMobile && !canCollapseOnMobile ? content : collapseWrapper(content);
};

export const DrawerSectionsWrapper: React.FC = ({ children }) => {
    const isMobile = useIsMobile();

    return (
        <Space
            direction="vertical"
            style={{ width: '100%', paddingBottom: 90 }}
            size={isMobile ? 8 : 0}
            split={
                <Divider
                    type="horizontal"
                    style={{
                        margin: 0,
                    }}
                />
            }
        >
            {children}
        </Space>
    );
};

export interface DrawerListItem {
    title: React.ReactNode;
    description?: React.ReactNode;
    onClick?: () => void;
}

export interface DrawerListProps {
    items: DrawerListItem[];
    emptyText?: string;
    isLoading?: boolean;
}

export const DrawerList: React.FC<DrawerListProps> = ({ items, emptyText, isLoading }) => {
    return (
        <List
            dataSource={compact(items)}
            split={false}
            className="drawer-list"
            loading={isLoading}
            bordered
            locale={{
                emptyText,
            }}
            renderItem={({ title, description, onClick }) => (
                <List.Item
                    key={title.toString()}
                    onClick={onClick}
                    style={{
                        backgroundColor: antGrey.grey2,
                        marginBottom: 2,
                        cursor: onClick ? 'pointer' : 'default',
                    }}
                >
                    {typeof title === 'string' ?
                        <Typography.Text
                            ellipsis
                            style={{
                                color: antGrey.grey7,
                                fontSize: 14,
                            }}
                        >
                            {title}
                        </Typography.Text>
                    :   title}
                    {typeof description === 'string' ?
                        <Typography.Text
                            ellipsis
                            style={{
                                color: antGrey.grey8,
                                fontSize: 14,
                                maxWidth: '70%',
                            }}
                        >
                            {description}
                        </Typography.Text>
                    :   description}
                </List.Item>
            )}
        />
    );
};
